// Fast-path quality (admin) — API client.
// Backend: app/routes/fastpath_quality_admin.py (/api/admin/fastpath-quality).
// Every shape mirrors a Pydantic model there. Two stored sources: the answer
// truth-set eval (fastpath_eval_runs / fastpath_eval_results) and the daily
// watchdog's judgments of real turns (fastpath_quality_checks), plus the
// executable fast recipes (fastpath_recipes / fastpath_recipe_runs). The page
// renders what was STORED — nothing is recomputed client-side.

import api from './api';

export type EvalRunStatus = 'running' | 'completed' | 'failed';
export type EvalTrigger = 'manual' | 'beat' | 'api';
export type EvalVerdict = 'correct' | 'partial' | 'incorrect' | 'no_answer' | 'handoff' | 'error';
export type WatchdogVerdict = 'faithful' | 'unfaithful' | 'no_answer' | 'unjudgeable';

export interface EvalRunRead {
  id: string;
  started_at: string;
  finished_at: string | null;
  trigger: EvalTrigger;
  status: EvalRunStatus;
  variant: Record<string, unknown>;
  truth_set_version: string;
  git_sha: string | null;
  cases_total: number;
  cases_passed: number;
  cases_partial: number;
  cases_failed: number;
  cases_handoff: number;
  cases_error: number;
  judge_model: string;
  /** Decimal serialised as a string by the API. */
  cost_usd: string;
  latency_p50_ms: number | null;
  latency_p95_ms: number | null;
  report: Record<string, unknown>;
  error: string | null;
}

export interface EvalToolCall {
  tool?: string;
  input?: unknown;
  result_excerpt?: string;
  [key: string]: unknown;
}

export interface EvalGrounding {
  ungrounded?: string[];
  ungrounded_numbers?: string[];
  action_claims?: string[];
  [key: string]: unknown;
}

export interface EvalResultRead {
  id: string;
  run_id: string;
  case_id: string;
  question: string;
  kind: string;
  routed_fast: boolean;
  verdict: EvalVerdict | string;
  reason: string | null;
  grounding: EvalGrounding | null;
  answer: string | null;
  expected: Record<string, unknown>;
  tool_calls: EvalToolCall[];
  fast_cost_usd: string;
  judge_cost_usd: string;
  latency_ms: number;
  error: string | null;
  created_at: string | null;
}

export interface EvalRunDetail extends EvalRunRead {
  results: EvalResultRead[];
}

export interface Paged<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}

export interface EvalRunDispatched {
  status: string;
  detail: string;
  task_id: string | null;
}

export interface WatchdogCheckRead {
  id: string;
  run_id: string;
  decision_id: string;
  conversation_id: string;
  prompt: string;
  verdict: WatchdogVerdict | string;
  reason: string | null;
  judge_model: string | null;
  replay_verdict: string | null;
  replay_reason: string | null;
  payload: Record<string, unknown> | null;
  created_at: string | null;
}

export interface WatchdogRunSummary {
  run_id: string;
  first_check_at: string | null;
  checks: number;
  verdicts: Record<string, number>;
  replays: Record<string, number>;
}

export type RecipeState = 'draft' | 'verified' | 'active' | 'disabled' | 'superseded';
export type RecipeRunTrigger = 'turn' | 'verify' | 'eval' | 'manual';
export type RecipeRunOutcome = 'ok' | 'error' | 'timeout' | 'budget' | 'invalid_params';

export interface RecipeTransition {
  from: string;
  to: string;
  reason: string;
  actor: string;
  at: string;
}

export interface RecipeVerification {
  verdict?: string;
  reason?: string;
  truth_case_id?: string | null;
  run_id?: string | null;
  at?: string;
  fail_streak?: number;
  last_transition?: RecipeTransition;
  [key: string]: unknown;
}

export interface RecipeRead {
  id: string;
  playbook_entry_id: string;
  profile_id: string;
  slug: string;
  name: string;
  description: string;
  question_examples: string[];
  params_schema: Record<string, unknown>;
  language: string;
  /** The first 400 characters of the code; the detail carries all of it. */
  code_preview: string;
  code_sha: string;
  state: RecipeState;
  version: number;
  superseded_by: string | null;
  author_kind: string;
  author_profile_id: string | null;
  author_conversation_id: string | null;
  verification: RecipeVerification | null;
  last_verified_at: string | null;
  last_run_at: string | null;
  run_count: number;
  consecutive_failures: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface RecipeRunRead {
  id: string;
  recipe_id: string;
  recipe_version: number;
  conversation_id: string | null;
  decision_id: string | null;
  trigger: RecipeRunTrigger | string;
  params: Record<string, unknown>;
  code_sha: string;
  result: unknown;
  result_bytes: number;
  rows_scanned: number | null;
  elapsed_ms: number;
  outcome: RecipeRunOutcome | string;
  error: string | null;
  created_at: string | null;
}

export interface RecipeDetail extends RecipeRead {
  code: string;
  runs: RecipeRunRead[];
}

export interface RecipeVerifyDispatched {
  queued: boolean;
  task_id: string | null;
}

const BASE = '/admin/fastpath-quality';

export const fastpathQualityApi = {
  async listEvalRuns(limit = 20, offset = 0): Promise<Paged<EvalRunRead>> {
    const r = await api.get<Paged<EvalRunRead>>(`${BASE}/eval-runs`, { params: { limit, offset } });
    return r.data;
  },
  /** The newest completed run with its results; null when none has completed yet. */
  async latestEvalRun(): Promise<EvalRunDetail | null> {
    try {
      const r = await api.get<EvalRunDetail>(`${BASE}/eval-runs/latest`);
      return r.data;
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },
  async getEvalRun(id: string): Promise<EvalRunDetail> {
    const r = await api.get<EvalRunDetail>(`${BASE}/eval-runs/${id}`);
    return r.data;
  },
  async runEval(variant?: Record<string, unknown>): Promise<EvalRunDispatched> {
    const r = await api.post<EvalRunDispatched>(`${BASE}/eval-runs`, variant ? { variant } : {});
    return r.data;
  },
  async listWatchdogRuns(limit = 30): Promise<WatchdogRunSummary[]> {
    const r = await api.get<{ items: WatchdogRunSummary[] }>(`${BASE}/watchdog-runs`, { params: { limit } });
    return r.data.items;
  },
  async listWatchdogChecks(opts: { runId?: string; verdict?: string; limit?: number; offset?: number } = {}): Promise<Paged<WatchdogCheckRead>> {
    const params: Record<string, string | number> = { limit: opts.limit ?? 50, offset: opts.offset ?? 0 };
    if (opts.runId) params.run_id = opts.runId;
    if (opts.verdict) params.verdict = opts.verdict;
    const r = await api.get<Paged<WatchdogCheckRead>>(`${BASE}/watchdog-checks`, { params });
    return r.data;
  },
  async listRecipes(opts: { state?: string; entry?: string; limit?: number; offset?: number } = {}): Promise<Paged<RecipeRead>> {
    const params: Record<string, string | number> = { limit: opts.limit ?? 100, offset: opts.offset ?? 0 };
    if (opts.state) params.state = opts.state;
    if (opts.entry) params.entry = opts.entry;
    const r = await api.get<Paged<RecipeRead>>(`${BASE}/recipes`, { params });
    return r.data;
  },
  async getRecipe(id: string): Promise<RecipeDetail> {
    const r = await api.get<RecipeDetail>(`${BASE}/recipes/${id}`);
    return r.data;
  },
  async setRecipeState(id: string, state: RecipeState, reason: string): Promise<RecipeRead> {
    const r = await api.post<RecipeRead>(`${BASE}/recipes/${id}/state`, { state, reason });
    return r.data;
  },
  async verifyRecipe(id: string): Promise<RecipeVerifyDispatched> {
    const r = await api.post<RecipeVerifyDispatched>(`${BASE}/recipes/${id}/verify`, {});
    return r.data;
  },
  async listRecipeRuns(id: string, limit = 20): Promise<RecipeRunRead[]> {
    const r = await api.get<{ items: RecipeRunRead[] }>(`${BASE}/recipes/${id}/runs`, { params: { limit } });
    return r.data.items;
  },
};

function isNotFound(err: unknown): boolean {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 404;
}
